import { UpperCasePipe } from '@angular/common';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { mfaCheckmarkIcon, mfaMailIcon, mfaPhoneIcon, mfaTextIcon } from '@ld.consumer-portal/data-uri';
import type { MfaOption } from '@ld.consumer-portal/mfa';
import { VerificationCodeCardComponent, type VerificationCodeSubmit } from '@ld.consumer-portal/mfa';
import { KeyboardService } from '@ld.consumer-portal/utils';
import type { MfaSendCodeRequest, MfaStep, MfaVerifyCodeRequest } from '../link-loan-mfa-modal/link-loan-mfa-modal.component';

export interface LinkLoanMfaDialogData {
  emailOptions: MfaOption[];
  textOptions: MfaOption[];
  callOptions: MfaOption[];
  isFirstTimeLinking: boolean;
  sendCode: (request: MfaSendCodeRequest) => Promise<string>;
  verifyCode: (request: MfaVerifyCodeRequest) => Promise<void>;
  resendCode: () => Promise<void>;
  confirmSuccess: () => Promise<void>;
}

export type LinkLoanMfaDialogResult = 'cancel' | 'success';

@Component({
  selector: 'ld-link-loan-mfa-dialog',
  templateUrl: './link-loan-mfa-dialog.component.html',
  styleUrl: './link-loan-mfa-dialog.component.scss',
  imports: [
    UpperCasePipe,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    RouterLink,
    VerificationCodeCardComponent,
    TranslocoDirective,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkLoanMfaDialogComponent {
  private readonly dialogRef = inject<DialogRef<LinkLoanMfaDialogResult>>(DialogRef);
  protected readonly data = inject<LinkLoanMfaDialogData>(DIALOG_DATA);
  private readonly transloco = inject(TranslocoService);
  private readonly keyboardService = inject(KeyboardService);

  @ViewChild('verificationCodeCard') verificationCodeCard?: VerificationCodeCardComponent;

  protected readonly checkmarkIcon = mfaCheckmarkIcon;
  protected readonly mailIcon = mfaMailIcon;
  protected readonly textIcon = mfaTextIcon;
  protected readonly phoneIcon = mfaPhoneIcon;

  protected readonly keyboardHeight = this.keyboardService.keyboardOverlayHeight;

  protected readonly currentStep = signal<MfaStep>('select-method');
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly maskedContact = signal('');
  protected readonly selectedVerificationType = signal<'email' | 'text' | 'call'>('email');

  protected readonly selectedBsEmail = signal<MfaOption | null>(null);
  protected readonly selectedBsPhone = signal<MfaOption | null>(null);
  protected readonly selectedBsCallPhone = signal<MfaOption | null>(null);

  protected readonly initialBsEmail = computed(() => this.data.emailOptions[0] ?? null);
  protected readonly initialBsPhone = computed(() => this.data.textOptions[0] ?? null);
  protected readonly initialBsCallPhone = computed(() => this.data.callOptions[0] ?? null);

  protected readonly isEmailOnly = computed(() =>
    this.data.emailOptions.length > 0 && this.data.textOptions.length === 0 && this.data.callOptions.length === 0,
  );

  constructor() {
    this.dialogRef.overlayRef.updateSize({ width: '100vw', maxWidth: '100vw' });
  }

  private readonly clearBsSelectionOnLoadingDone = effect(() => {
    if (!this.isLoading()) {
      this.selectedBsEmail.set(null);
      this.selectedBsPhone.set(null);
      this.selectedBsCallPhone.set(null);
    }
  });

  protected getMfaDescription(): string | null {
    if (this.isEmailOnly()) {
      return this.transloco.translate('mfaLinkLoanEmailOnlyDescription');
    }
    return null;
  }

  protected getVerificationTypeDisplay(): 'email' | 'text' | 'call' {
    return this.selectedVerificationType();
  }

  protected async onSelectEmail(): Promise<void> {
    const option = this.selectedBsEmail() ?? this.initialBsEmail();
    if (option) {
      this.selectedVerificationType.set('email');
      await this.sendCode({ contactId: option.value, type: 'email' });
    }
  }

  protected async onSelectText(): Promise<void> {
    const option = this.selectedBsPhone() ?? this.initialBsPhone();
    if (option) {
      this.selectedVerificationType.set('text');
      await this.sendCode({ contactId: option.value, type: 'sms' });
    }
  }

  protected async onSelectCall(): Promise<void> {
    const option = this.selectedBsCallPhone() ?? this.initialBsCallPhone();
    if (option) {
      this.selectedVerificationType.set('call');
      await this.sendCode({ contactId: option.value, type: 'call' });
    }
  }

  protected async onVerify(event: VerificationCodeSubmit): Promise<void> {
    this.isLoading.set(true);
    try {
      await this.data.verifyCode({ code: event.code, rememberDevice: event.rememberDevice });
      this.currentStep.set('success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Verification failed. Please try again.';
      this.verificationCodeCard?.setError(message);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected async onResend(): Promise<void> {
    try {
      await this.data.resendCode();
      this.verificationCodeCard?.setSuccess('A new verification code has been sent.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to resend code. Please try again.';
      this.verificationCodeCard?.setError(message);
    }
  }

  protected onVerificationCancel(): void {
    this.currentStep.set('select-method');
  }

  protected onCancel(): void {
    if (this.isLoading()) return;
    this.dialogRef.close('cancel');
  }

  protected async onConfirmSuccess(): Promise<void> {
    this.isLoading.set(true);
    try {
      await this.data.confirmSuccess();
      this.dialogRef.close('success');
    } catch {
      this.isLoading.set(false);
    }
  }

  private async sendCode(request: MfaSendCodeRequest): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    try {
      const masked = await this.data.sendCode(request);
      this.maskedContact.set(masked);
      this.currentStep.set('enter-code');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unable to send code. Please try again.';
      this.errorMessage.set(message);
    } finally {
      this.isLoading.set(false);
    }
  }
}
